import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Palette, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Frame,
  Brush,
  Image,
  Layers,
  PenTool,
  Ruler,
  Home,
  Building,
  Gift,
  User
} from 'lucide-react';
import { toast } from 'sonner';

// Painting types
const PAINTING_TYPES = [
  { value: 'ritratto', label: 'Ritratto / Portrait', icon: User },
  { value: 'paesaggio', label: 'Paesaggio', icon: Image },
  { value: 'astratto', label: 'Astratto', icon: Layers },
  { value: 'natura_morta', label: 'Natura morta', icon: Frame },
  { value: 'animali', label: 'Animali / Pet portrait', icon: Heart },
  { value: 'architettura', label: 'Architettura / Veduta', icon: Building },
  { value: 'figurativo', label: 'Figurativo', icon: User },
  { value: 'moderno', label: 'Arte moderna / Contemporanea', icon: Sparkles },
  { value: 'classico', label: 'Stile classico / Accademico', icon: Frame },
  { value: 'illustrazione', label: 'Illustrazione', icon: PenTool },
  { value: 'riproduzione', label: 'Riproduzione opera famosa', icon: Image },
  { value: 'altro', label: 'Altro', icon: Palette },
];

// Technique preferences
const TECHNIQUES = [
  { value: 'olio', label: 'Olio su tela', icon: Brush },
  { value: 'acrilico', label: 'Acrilico', icon: Brush },
  { value: 'acquerello', label: 'Acquerello', icon: Brush },
  { value: 'tempera', label: 'Tempera', icon: Brush },
  { value: 'pastello', label: 'Pastello', icon: PenTool },
  { value: 'carboncino', label: 'Carboncino / Grafite', icon: PenTool },
  { value: 'tecnica_mista', label: 'Tecnica mista', icon: Layers },
  { value: 'digitale', label: 'Arte digitale', icon: Image },
  { value: 'affresco', label: 'Affresco / Murale', icon: Building },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Purpose/Destination
const PAINTING_PURPOSES = [
  { value: 'casa_soggiorno', label: 'Per il soggiorno', icon: Home },
  { value: 'casa_camera', label: 'Per la camera da letto', icon: Home },
  { value: 'ufficio', label: 'Per l\'ufficio / Studio', icon: Building },
  { value: 'regalo', label: 'Come regalo', icon: Gift },
  { value: 'collezione', label: 'Per la mia collezione', icon: Frame },
  { value: 'commemorativo', label: 'Ritratto commemorativo', icon: Heart },
  { value: 'ristorante', label: 'Per ristorante / Hotel', icon: Building },
  { value: 'evento', label: 'Per un evento speciale', icon: Sparkles },
  { value: 'altro', label: 'Altro', icon: Palette },
];

// Size options
const SIZE_OPTIONS = [
  { value: 'piccolo', label: 'Piccolo (fino a 30x40 cm)', description: 'Ideale per angoli' },
  { value: 'medio', label: 'Medio (40x50 - 50x70 cm)', description: 'Standard da parete' },
  { value: 'grande', label: 'Grande (60x80 - 80x100 cm)', description: 'Statement piece' },
  { value: 'extra_large', label: 'Extra Large (100x150+ cm)', description: 'Impatto scenografico' },
  { value: 'personalizzato', label: 'Misure personalizzate', description: 'Su indicazione' },
  { value: 'non_so', label: 'Non so ancora', description: 'Vorrei consiglio' },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '100-300', label: '€100 - €300', description: 'Piccole opere' },
  { value: '300-600', label: '€300 - €600', description: 'Opere medie' },
  { value: '600-1000', label: '€600 - €1.000', description: 'Opere importanti' },
  { value: '1000-2000', label: '€1.000 - €2.000', description: 'Commissioni elaborate' },
  { value: '2000-5000', label: '€2.000 - €5.000', description: 'Grandi formati' },
  { value: '5000+', label: '€5.000+', description: 'Opere d\'autore / Murali' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '2weeks', label: '2 Settimane', description: 'Urgente' },
  { value: '1month', label: '1 Mese', description: 'Prioritario' },
  { value: '2months', label: '2 Mesi', description: 'Standard' },
  { value: '3months', label: '3 Mesi', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example paintings
const PAINTING_EXAMPLES = [
  {
    idea: "Ritratto di famiglia da foto",
    result: "Olio su tela 60x80cm con 4 soggetti in stile impressionista moderno",
    image: "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=400&h=300&fit=crop",
    category: "Ritratti"
  },
  {
    idea: "Paesaggio toscano per soggiorno",
    result: "Acrilico su tela 100x70cm con colline, cipressi e casale al tramonto",
    image: "https://images.unsplash.com/photo-1578926288207-a90a5366759d?w=400&h=300&fit=crop",
    category: "Paesaggi"
  },
  {
    idea: "Quadro astratto sui toni del blu",
    result: "Tecnica mista 80x80cm con texture materiche e sfumature blu/oro",
    image: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=300&fit=crop",
    category: "Astratto"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho commissionato un ritratto dei miei genitori. L'artista ha catturato perfettamente le loro espressioni. Un regalo indimenticabile.",
    author: "Francesca M.",
    location: "Roma"
  },
  {
    text: "Cercavo un quadro grande per il soggiorno. L'artista ha creato un'opera che si integra perfettamente con l'arredamento.",
    author: "Marco e Giulia",
    location: "Milano"
  },
  {
    text: "Il mio cane è immortalato in un bellissimo ritratto ad olio. Qualità incredibile e artista molto disponibile.",
    author: "Andrea R.",
    location: "Firenze"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "È gratuito inviare una richiesta?",
    answer: "Sì, è completamente gratuito. Descrivi l'opera che desideri e ricevi proposte da artisti senza alcun costo."
  },
  {
    question: "Sono obbligato ad acquistare?",
    answer: "Assolutamente no. Ricevi le proposte, le valuti con calma e decidi solo se trovi l'artista e lo stile perfetto per te."
  },
  {
    question: "Posso vedere bozzetti prima dell'opera finale?",
    answer: "Certamente! Molti artisti inviano schizzi preliminari o studi per approvazione prima di iniziare l'opera definitiva."
  },
  {
    question: "Quanto tempo ci vuole per realizzare un dipinto?",
    answer: "Dipende dalla complessità e dalla tecnica. Un ritratto medio richiede 2-4 settimane, un grande formato può richiedere 2-3 mesi."
  },
  {
    question: "Posso richiedere modifiche durante la lavorazione?",
    answer: "Sì, la maggior parte degli artisti prevede fasi di revisione. Discuterai i dettagli direttamente con l'artista scelto."
  }
];

export default function PitturaRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    painting_type: '',
    technique: '',
    purpose: '',
    size: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Dipinti su Commissione | Artisti Italiani | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `painting-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi l\'opera che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_painting_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const paintingTypeLabel = PAINTING_TYPES.find(t => t.value === formData.painting_type)?.label || '';
      const techniqueLabel = TECHNIQUES.find(t => t.value === formData.technique)?.label || '';
      const purposeLabel = PAINTING_PURPOSES.find(p => p.value === formData.purpose)?.label || '';
      const sizeLabel = SIZE_OPTIONS.find(s => s.value === formData.size)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (paintingTypeLabel) details.push(`🖼️  Tipo opera: ${paintingTypeLabel}`);
      if (techniqueLabel) details.push(`🎨  Tecnica: ${techniqueLabel}`);
      if (purposeLabel) details.push(`🏛️  Destinazione: ${purposeLabel}`);
      if (sizeLabel) details.push(`📐  Dimensioni: ${sizeLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${paintingTypeLabel} - ${techniqueLabel}`.trim() || 'Dipinto su commissione',
        description: fullDescription,
        category: 'Pittura',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli artisti.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('painting-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Dipinti su Commissione | Quadri e Ritratti da Artisti Italiani | Rarifex</title>
        <meta name="description" content="Commissiona un dipinto unico da artisti italiani. Ritratti, paesaggi, astratti, olio su tela. Descrivi la tua idea e ricevi proposte personalizzate. Gratuito e senza impegno." />
        <meta name="keywords" content="dipinto su commissione, quadro personalizzato, ritratto su commissione, pittore italiano, olio su tela, acquerello, arte su misura, quadro su ordinazione" />
        <link rel="canonical" href="https://www.rarifex.com/dipinti-su-commissione" />
        <meta property="og:title" content="Dipinti su Commissione | Artisti Italiani | Rarifex" />
        <meta property="og:description" content="Commissiona un dipinto unico da artisti italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/dipinti-su-commissione" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 via-white to-purple-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Palette className="w-4 h-4" />
                    Dipinti su Commissione
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    L'opera d'arte{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                      che hai sempre sognato
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Artisti italiani la dipingono apposta per te.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Descrivi l'opera che immagini e ricevi proposte personalizzate da pittori e artisti esperti.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artisti professionisti</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-indigo-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="painting-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Ritratto di famiglia in stile impressionista"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="painting-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi l'opera che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Soggetto, stile, colori dominanti, atmosfera. Se hai foto di riferimento (per ritratti), potrai allegarle sotto."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="painting-description-textarea"
                        />
                      </div>
                      
                      {/* Painting Type & Technique */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Tipo di opera
                          </Label>
                          <select
                            value={formData.painting_type}
                            onChange={(e) => handleInputChange('painting_type', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            data-testid="painting-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {PAINTING_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Tecnica preferita
                          </Label>
                          <select
                            value={formData.technique}
                            onChange={(e) => handleInputChange('technique', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            data-testid="painting-technique-select"
                          >
                            <option value="">Seleziona...</option>
                            {TECHNIQUES.map(tech => (
                              <option key={tech.value} value={tech.value}>{tech.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Purpose & Size */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Destinazione</Label>
                          <select
                            value={formData.purpose}
                            onChange={(e) => handleInputChange('purpose', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            data-testid="painting-purpose-select"
                          >
                            <option value="">Seleziona...</option>
                            {PAINTING_PURPOSES.map(purpose => (
                              <option key={purpose.value} value={purpose.value}>{purpose.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Dimensioni</Label>
                          <select
                            value={formData.size}
                            onChange={(e) => handleInputChange('size', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            data-testid="painting-size-select"
                          >
                            <option value="">Seleziona...</option>
                            {SIZE_OPTIONS.map(size => (
                              <option key={size.value} value={size.value}>
                                {size.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Budget & Timeline */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            data-testid="painting-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tempi</Label>
                          <select
                            value={formData.timeline}
                            onChange={(e) => handleInputChange('timeline', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            data-testid="painting-timeline-select"
                          >
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Attachments */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Foto di riferimento (importante per ritratti!)
                        </Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploadingFile}
                                data-testid="painting-file-upload"
                              />
                              {uploadingFile ? (
                                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              ) : (
                                <Upload className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="text-sm text-slate-500">
                                Carica foto o ispirazioni ({formData.attachments.length}/5)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg shadow-indigo-200"
                        data-testid="painting-submit-button"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Palette className="w-5 h-5 mr-2" />
                        )}
                        Ricevi proposte gratis
                      </Button>
                      
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROBLEMA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                L'arte che cerchi non si trova nelle gallerie
              </h2>
              
              <div className="space-y-4 text-lg text-slate-600 mb-8">
                <p className="flex items-center justify-center gap-3">
                  <span className="text-indigo-400">✗</span>
                  I quadri in vendita non rispecchiano i tuoi gusti
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-indigo-400">✗</span>
                  Vorresti un ritratto ma non sai a chi rivolgerti
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-indigo-400">✗</span>
                  Le stampe non trasmettono l'emozione di un'opera originale
                </p>
              </div>
              
              <p className="text-xl text-slate-700 font-medium">
                Per un'opera che parli di te, serve un artista.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: SOLUZIONE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Un'opera{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  creata solo per te
                </span>
              </h2>
              
              <p className="text-xl text-slate-600 mb-12">
                Pittori e artisti italiani pronti a trasformare la tua idea in un capolavoro.
                <br />
                <strong className="text-slate-800">Tu sogni, loro dipingono.</strong>
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-indigo-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Descrivi la tua visione</h3>
                  <p className="text-slate-600">Racconta l'opera che immagini, stile e colori</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-purple-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Artisti ti propongono</h3>
                  <p className="text-slate-600">Ricevi bozzetti e preventivi da pittori esperti</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Scegli il tuo artista</h3>
                  <p className="text-slate-600">Nessun obbligo, decidi solo se ti emoziona</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: ESEMPI */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
                Da idea a capolavoro
              </h2>
              <p className="text-xl text-slate-600 text-center mb-12">
                Ecco cosa hanno creato i nostri artisti
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {PAINTING_EXAMPLES.map((example, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <img 
                      src={example.image} 
                      alt={example.result}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <span className="text-xs font-medium text-indigo-500 uppercase tracking-wider">
                        {example.category}
                      </span>
                      <p className="text-sm text-slate-500 mt-2 mb-1">
                        Richiesta: "{example.idea}"
                      </p>
                      <p className="font-semibold text-slate-800">
                        → {example.result}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button 
                  onClick={scrollToForm}
                  variant="outline"
                  className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                >
                  Vuoi qualcosa di simile? Descrivi la tua idea
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5: COME FUNZIONA */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-indigo-50/50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                In 3 semplici passaggi
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Descrivi l'opera</h3>
                    <p className="text-slate-600">
                      Racconta cosa vuoi: soggetto, stile, dimensioni, colori. Per i ritratti, allega le foto da cui partire.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevi proposte</h3>
                    <p className="text-slate-600">
                      Pittori e artisti vedono la tua richiesta e ti inviano portfolio, bozzetti preliminari e preventivi.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Scegli l'artista perfetto</h3>
                    <p className="text-slate-600">
                      Confronta gli stili, chatta con gli artisti per affinare i dettagli. Nessun obbligo, scegli solo se sei convinto.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: PERCHÉ FUNZIONA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Perché commissionare un dipinto
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-indigo-50">
                  <CheckCircle className="w-8 h-8 text-indigo-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Opera unica</h3>
                    <p className="text-sm text-slate-600">Nessuno avrà mai lo stesso quadro</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-purple-50">
                  <Sparkles className="w-8 h-8 text-purple-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Personalizzazione totale</h3>
                    <p className="text-sm text-slate-600">Soggetto, stile, dimensioni come vuoi tu</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50">
                  <Shield className="w-8 h-8 text-blue-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Valore nel tempo</h3>
                    <p className="text-sm text-slate-600">Un'opera originale che dura per sempre</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-green-50">
                  <Zap className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Zero rischio</h3>
                    <p className="text-sm text-slate-600">Nessun obbligo di acquisto</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Cosa dicono i clienti
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {TESTIMONIALS.map((testimonial, idx) => (
                  <Card key={idx} className="p-6 bg-white">
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className="w-5 h-5 fill-indigo-400 text-indigo-400" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center font-semibold text-indigo-600">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-medium">{testimonial.author}</p>
                        <p className="text-slate-500 text-xs">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Domande frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                      data-testid={`painting-faq-${idx}`}
                    >
                      <span className="font-medium text-slate-800">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9: CTA FINALE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-indigo-50 to-purple-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Palette className="w-16 h-16 text-indigo-500 mx-auto mb-6" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Pronto a creare la tua opera d'arte?
              </h2>
              
              <p className="text-xl text-slate-600 mb-8">
                Gli artisti italiani aspettano la tua ispirazione.
              </p>
              
              <Button
                onClick={scrollToForm}
                size="lg"
                className="py-6 px-10 text-lg font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg shadow-indigo-200"
                data-testid="painting-cta-final-button"
              >
                <Palette className="w-5 h-5 mr-2" />
                Ricevi proposte gratis ora
              </Button>
            </div>
          </div>
        </section>

      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">
              Per inviare la tua richiesta e ricevere proposte dagli artisti, devi prima accedere o creare un account.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAuthPrompt(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={() => navigate('/login?redirect=/dipinti-su-commissione')}
                className="flex-1 bg-indigo-500 hover:bg-indigo-600"
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
